import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../api/axiosConfig';
import { useToast } from '../../hooks/useToast';
import { MapPin, Calendar, Clock, User, Users, Phone, Plane, FileText, CheckCircle, Navigation } from 'lucide-react';

// Material Icons CSS import
const MaterialIcons = () => (
  <>
    <link 
      href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" 
      rel="stylesheet" 
    />
    <link 
      href="https://fonts.googleapis.com/icon?family=Material+Icons" 
      rel="stylesheet" 
    />
  </>
);

// AssignmentCard component'ini top-level'a taşıdık - klavye kapanma sorunu için
const AssignmentCard = React.memo(({
  assignment,
  getAvailableActions,
  getAgencyBadgeColor,
  formatDate,
  formatTime,
  getServiceTypeText,
  handleStatusUpdate,
  setAssignments,
  renderNotesWithPhoneLinks
}) => {
  const actions = getAvailableActions(assignment.status, assignment);
  
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header - Turuncu Gradient */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-white text-2xl font-bold">{assignment.agency_name || 'BHT'}</div>
            <div className="text-orange-100 text-sm mt-1">Transfer Görevi</div>
          </div>
          <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="text-white font-semibold">#{assignment.assignment_id}</span>
          </div>
        </div>
      </div>

      {/* Tarih ve Saat */}
      <div className="bg-orange-50 px-5 py-3 border-b border-orange-100">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-orange-600" />
          <span className="font-semibold text-gray-900">{formatDate(assignment.pickup_date)}</span>
          <Clock className="w-5 h-5 text-orange-600 ml-auto" />
          <span className="font-bold text-orange-600 text-lg">{formatTime(assignment.pickup_time)}</span>
        </div>
      </div>

      {/* Rota */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <div className="w-0.5 h-full bg-gray-300 my-1"></div>
            </div>
            <div className="flex-1 pb-2">
              <div className="text-xs text-gray-500 mb-1">ALIŞ NOKTASI</div>
              <div className="font-semibold text-gray-900">{assignment.pickup_addr}</div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">BIRAKIŞ NOKTASI</div>
              <div className="font-semibold text-gray-900 text-sm leading-snug">{assignment.dropoff_addr}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Hasta Bilgileri */}
      {(assignment.passenger_name || assignment.passenger_id_no || assignment.service_type) && (
        <div className="px-5 py-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Hasta Bilgileri</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Hizmet Tipi:</span>
              <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
                {getServiceTypeText(assignment.service_type) || 'Belirtilmemiş'}
              </span>
            </div>
            {assignment.passenger_name && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Hasta:</div>
                <div className="font-bold text-gray-900">{assignment.passenger_name}</div>
              </div>
            )}
            <div className="flex justify-between items-center">
              {assignment.passenger_id_no && (
                <div>
                  <div className="text-xs text-gray-500">Kimlik No</div>
                  <div className="font-semibold text-gray-900">{assignment.passenger_id_no}</div>
                </div>
              )}
              <div className="text-right">
                <div className="text-xs text-gray-500">Kişi Sayısı</div>
                <div className="font-semibold text-gray-900 flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {assignment.pax_count} kişi
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Uçuş Kodu - Sadece Havalimanı Transferi için */}
      {assignment.service_type === 'AIRPORT' && assignment.flight_code && (
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 bg-purple-50 p-3 rounded-lg">
            <Plane className="w-5 h-5 text-purple-600" />
            <div>
              <div className="text-xs text-gray-500">Uçuş Kodu</div>
              <div className="font-bold text-purple-900">{assignment.flight_code}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sorumlu Kişi Bilgileri */}
      {(assignment.responsible_person || assignment.responsible_contact) && (
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-green-600" />
              <span className="text-xs font-semibold text-green-900 uppercase tracking-wide">Sorumlu Kişi</span>
            </div>
            <div className="space-y-1">
              {assignment.responsible_person && (
                <div className="font-semibold text-gray-900">{assignment.responsible_person}</div>
              )}
              {assignment.responsible_contact && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Phone className="w-4 h-4 text-green-600" />
                  <a 
                    href={`tel:${assignment.responsible_contact}`} 
                    className="font-medium hover:text-green-600 transition-colors"
                  >
                    {assignment.responsible_contact}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notlar */}
      {assignment.notes && (
        <div className="px-5 py-4 bg-yellow-50 border-b border-yellow-100">
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs font-semibold text-yellow-900 uppercase tracking-wide mb-1">Notlar</div>
              <div className="text-sm text-gray-700 leading-relaxed">{renderNotesWithPhoneLinks(assignment.notes)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Araç Plakası Girişi - PLANNED durumunda */}
      {assignment.status === 'PLANNED' && (
        <div className="px-5 py-4 border-b border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Navigation className="w-4 h-4" />
              Araç Plaka No
            </div>
          </label>
          <input
            type="text"
            placeholder="Örn: 34 ABC 123"
            value={assignment.vehicle_no || ''}
            onChange={(e) => {
              const newValue = e.target.value.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
              setAssignments(prev => prev.map(a => 
                a.assignment_id === assignment.assignment_id 
                  ? { ...a, vehicle_no: newValue }
                  : a
              ));
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-center font-semibold text-gray-800 placeholder-gray-400"
            style={{ textTransform: 'uppercase' }}
            maxLength={15}
          />
          <p className="text-xs text-gray-500 mt-1 text-center">
            Plaka numarasını girdikten sonra "Yola Çıkıyorum" butonu aktif olacak
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-5 py-4">
        {actions.length > 0 ? (
          <button
            onClick={() => handleStatusUpdate(assignment.assignment_id, actions[0].type)}
            className={`w-full py-4 rounded-xl font-bold text-white transition-all duration-300 flex items-center justify-center gap-2 ${actions[0].color} hover:shadow-lg`}
          >
            <CheckCircle className="w-5 h-5" />
            {actions[0].label}
          </button>
        ) : assignment.status === 'COMPLETED' ? (
          <div className="w-full py-4 rounded-xl font-bold text-white bg-green-500 shadow-lg shadow-green-500/50 flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Transfer Tamamlandı ✓
          </div>
        ) : assignment.status === 'PLANNED' && (!assignment.vehicle_no || assignment.vehicle_no.trim().length === 0) ? (
          <div className="w-full py-4 rounded-xl font-medium text-blue-700 bg-blue-50 border border-blue-200 flex items-center justify-center gap-2">
            <Navigation className="w-5 h-5" />
            Önce plakayı girin
          </div>
        ) : null}
      </div>
    </div>
  );
});

const Driver_Home = ({ user, onLogout }) => {
  const { showSuccess, showError } = useToast();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previousAssignmentCount, setPreviousAssignmentCount] = useState(0);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  // Plaka bilgilerini basit state ile yönet - acente portalı mantığı

  // Browser notification izni iste (ilk yüklemede)
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Bildirim fonksiyonu (çift beep + toast + browser notification)
  const playNotification = useCallback((message) => {
    if (!notificationEnabled) return;
    
    try {
      // 1. Çift Beep Sesi (WhatsApp tarzı)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // İlk beep
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.frequency.value = 800;
      oscillator1.type = 'sine';
      
      gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.15);
      
      // İkinci beep (0.2 saniye sonra)
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.value = 800;
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.2);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35);
      
      oscillator2.start(audioContext.currentTime + 0.2);
      oscillator2.stop(audioContext.currentTime + 0.35);
      
      // 2. Toast göster
      showSuccess(message);
      
      // 3. Browser notification (izin varsa)
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("BHT Transfer - Şoför", {
          body: message,
          icon: "/favicon.ico",
          badge: "/favicon.ico"
        });
      }
    } catch (error) {
      console.error('Bildirim hatası:', error);
      showSuccess(message);
    }
  }, [notificationEnabled, showSuccess]);

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Local timezone için doğru tarih
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const response = await api.get(`/api/driver/assignments?target_date=${today}`);
      
      const newAssignments = response.data.assignments || [];
      
      // Sadece gerçekten değişiklik varsa state'i güncelle
      setAssignments(prevAssignments => {
        const hasChanged = JSON.stringify(prevAssignments) !== JSON.stringify(newAssignments);
        return hasChanged ? newAssignments : prevAssignments;
      });
    } catch (error) {
      console.error('Assignments loading error:', error);
      if (error.response?.status === 401) {
        onLogout();
        return;
      }
      setError('Görevler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    loadAssignments();
    
    // Auto-refresh (30 saniyede bir) - bildirim için gerekli
    const interval = setInterval(() => {
      loadAssignments();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Assignment sayısı değiştiğinde bildirim kontrolü
  useEffect(() => {
    if (previousAssignmentCount > 0 && assignments.length > previousAssignmentCount) {
      const diff = assignments.length - previousAssignmentCount;
      playNotification(`🔔 Size ${diff} yeni transfer atandı!`);
    }
    
    // Assignment sayısını güncelle
    if (assignments.length > 0) {
      setPreviousAssignmentCount(assignments.length);
    }
  }, [assignments.length, playNotification, previousAssignmentCount]);

  // updateVehicleNumber fonksiyonu test için kaldırıldı

  const updateTransferVehicleNumber = async (assignmentId, vehicleNo) => {
    try {
      // Şoför endpoint'i ile vehicle_no güncelle
      await api.put(`/api/driver/assignments/${assignmentId}/vehicle`, {
        vehicle_no: vehicleNo
      });
      console.log(`✅ Araç plakası güncellendi: ${vehicleNo}`);
    } catch (error) {
      console.error('Vehicle number update error:', error);
      showError('Araç plakası kaydedilemedi, ancak transfer devam edecek');
      // Hata olsa bile devam et, kritik değil
    }
  };

  const handleStatusUpdate = async (assignmentId, eventType) => {
    try {
      // "Yola Çıkıyorum" durumunda plaka bilgisini DB'ye kaydet
      if (eventType === 'enroute') {
        const assignment = assignments.find(a => a.assignment_id === assignmentId);
        const vehicleInfo = assignment?.vehicle_no;
        if (vehicleInfo && vehicleInfo.trim()) {
          await updateTransferVehicleNumber(assignmentId, vehicleInfo.trim());
        }
      }

      await api.post(`/api/driver/assignments/${assignmentId}/event`, {
        event_type: eventType
      });

      // Harita açma işlemi
      const assignment = assignments.find(a => a.assignment_id === assignmentId);
      if (assignment) {
        let mapUrl = '';
        
        switch (eventType) {
          case 'enroute':
            // Mevcut konumdan alma noktasına
            mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(assignment.pickup_addr)}`;
            break;
          case 'picked_up':
            // Alma noktasından bırakma noktasına
            mapUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(assignment.pickup_addr)}&destination=${encodeURIComponent(assignment.dropoff_addr)}`;
            break;
        }
        
        if (mapUrl) {
          // BASİT VE ETKİLİ NAVİGASYON
          try {
            // Mobile cihazlarda direkt location.href kullan
            if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
              window.location.href = mapUrl;
            } else {
              // Desktop'ta yeni sekme
              window.open(mapUrl, '_blank');
            }
            console.log('🗺️ Harita açılıyor:', mapUrl);
          } catch (error) {
            console.error('Navigasyon hatası:', error);
            // Fallback: Alert ile URL göster
            alert(`Harita açılamadı. Manuel olarak açın:\n${mapUrl}`);
          }
        }
      }

      // Başarı mesajı
      const messages = {
        'enroute': 'Yola çıkma durumu güncellendi',
        'at_pickup': 'Konumda olduğunuz kaydedildi',
        'picked_up': 'Misafiri alma durumu güncellendi', 
        'completed': 'Transfer tamamlandı'
      };
      
      showSuccess(messages[eventType] || 'Durum güncellendi');
      
      // Listeyi yenile
      loadAssignments();
      
    } catch (error) {
      console.error('Status update error:', error);
      showError(error.response?.data?.detail || 'Durum güncellenemedi');
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'RECEIVED': 'Talep Alındı',
      'PLANNED': 'Planlandı',
      'ENROUTE': 'Yola Çıktım',
      'AT_PICKUP': 'Konumdayım',
      'PICKED_UP': 'Yolcuyu Aldım',
      'COMPLETED': 'Tamamlandı'
    };
    return statusMap[status] || status;
  };

  const getServiceTypeText = (serviceType) => {
    const serviceMap = {
      'PATIENT': 'HASTA',
      'STAFF': 'PERSONEL',
      'EXECUTIVE': 'YÖNETİCİ',
      'DOCUMENT': 'EVRAK',
      'AIRPORT': 'HAVALİMANI TRANSFER',
      'MATERIAL': 'MALZEME'
    };
    return serviceMap[serviceType] || serviceType;
  };

  const getAgencyBadgeColor = useCallback((agencyName) => {
    if (!agencyName || agencyName === 'BHT Clinic Admin') {
      return 'bg-blue-100 text-blue-800';
    }
    // Farklı acenteler için farklı renkler
    const colors = [
      'bg-purple-100 text-purple-800',
      'bg-green-100 text-green-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800'
    ];
    const hash = agencyName.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }, []);

  const getAvailableActions = (status, assignment) => {
    switch (status) {
      case 'PLANNED':
        // Plaka/araç bilgisi girilmeden "Yola Çıkıyorum" butonu gösterme
        if (!assignment.vehicle_no || assignment.vehicle_no.trim().length === 0) {
          return [];
        }
        return [
          { type: 'enroute', label: 'Yola Çıkıyorum', color: 'bg-blue-500 hover:bg-blue-600', icon: 'directions_car' },
        ];
      case 'ENROUTE':
        return [
          { type: 'at_pickup', label: 'Konumdayım', color: 'bg-purple-500 hover:bg-purple-600', icon: 'location_on' },
        ];
      case 'AT_PICKUP':
        return [
          { type: 'picked_up', label: 'Yolcuyu Aldım', color: 'bg-orange-500 hover:bg-orange-600', icon: 'person_add' },
        ];
      case 'PICKED_UP':
        return [
          { type: 'completed', label: 'Yolcuyu Bıraktım', color: 'bg-green-500 hover:bg-green-600', icon: 'check_circle' },
        ];
      case 'COMPLETED':
        return [];
      default:
        return [];
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    return timeStr ? timeStr.substring(0, 5) : '';
  };

  // Telefon numaralarını tespit edip tıklanabilir hale getir
  const renderNotesWithPhoneLinks = (notes) => {
    if (!notes) return null;

    // Türkiye telefon numarası formatları: 05XXXXXXXXX, +905XXXXXXXXX, 0(5XX)XXXXXXX, 5XXXXXXXXX
    const phoneRegex = /(\+90\s?|0)?\s?\(?\s?5\d{2}\s?\)?\s?\d{3}\s?\d{2}\s?\d{2}|\b5\d{9}\b/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = phoneRegex.exec(notes)) !== null) {
      // Regex match'ten önceki metni ekle
      if (match.index > lastIndex) {
        parts.push(notes.substring(lastIndex, match.index));
      }

      // Telefon numarasını temizle (sadece rakamlar)
      const rawPhone = match[0].replace(/\D/g, '');

      // Türkiye formatına normalize et (0 ile başlayan 11 haneli)
      let normalizedPhone = rawPhone;
      if (rawPhone.startsWith('90') && rawPhone.length === 12) {
        normalizedPhone = '0' + rawPhone.substring(2);
      } else if (rawPhone.length === 10 && rawPhone.startsWith('5')) {
        normalizedPhone = '0' + rawPhone;
      }

      // Tıklanabilir link ekle
      parts.push(
        <a
          key={match.index}
          href={`tel:+90${normalizedPhone.substring(1)}`}
          className="text-blue-600 hover:text-blue-800 font-semibold underline"
          onClick={(e) => {
            // Mobil cihazlarda direkt arama yap
            e.stopPropagation();
          }}
        >
          {match[0]}
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    // Kalan metni ekle
    if (lastIndex < notes.length) {
      parts.push(notes.substring(lastIndex));
    }

    return parts.length > 0 ? parts : notes;
  };

  // Tarihe göre gruplama fonksiyonu
  const groupAssignmentsByDate = (assignments) => {
    const grouped = {};
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;

    assignments.forEach(assignment => {
      const dateKey = assignment.pickup_date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(assignment);
    });

    // Tarihleri sırala
    return Object.keys(grouped)
      .sort()
      .map(dateKey => ({
        date: dateKey,
        label: dateKey === today ? 'Bugün' : (dateKey === tomorrowStr ? 'Yarın' : formatDate(dateKey)),
        assignments: grouped[dateKey]
      }));
  };

  // AssignmentCard artık top-level'da tanımlı - duplikasyon kaldırıldı

  const EmptyState = () => (
    <div className="text-center py-16">
      <div className="mx-auto w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-6 shadow-lg">
        <span className="text-5xl">📋</span>
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">Bugün atamanız yok</h3>
      <p className="text-gray-500">Yeni atamalar geldiğinde burada görünecek</p>
    </div>
  );

  // Tarihe göre grupla ve render et
  const groupedAssignments = useMemo(() => {
    return groupAssignmentsByDate(assignments);
  }, [assignments]);

  // Assignment kartlarını useMemo ile optimize et - gerekli props'ları geç
  const renderGroupedAssignments = useMemo(() => {
    return groupedAssignments.map((group) => (
      <div key={group.date} className="mb-8">
        {/* Tarih Başlığı */}
        <div className={`sticky top-0 z-10 mb-4 py-3 px-4 rounded-lg shadow-sm ${
          group.label === 'Bugün'
            ? 'bg-gradient-to-r from-blue-500 to-blue-600'
            : 'bg-gradient-to-r from-gray-500 to-gray-600'
        }`}>
          <h3 className="text-lg font-bold text-white flex items-center">
            <span className="material-icons mr-2">
              {group.label === 'Bugün' ? 'today' : 'event'}
            </span>
            {group.label}
            <span className="ml-auto bg-white bg-opacity-20 text-white text-sm font-semibold px-3 py-1 rounded-full">
              {group.assignments.length} transfer
            </span>
          </h3>
          <p className="text-sm text-white text-opacity-90 mt-1">
            {formatDate(group.date)}
          </p>
        </div>

        {/* Bu tarihteki transferler */}
        <div className="space-y-4">
          {group.assignments.map(assignment => (
            <AssignmentCard
              key={assignment.assignment_id}
              assignment={assignment}
              getAvailableActions={getAvailableActions}
              getAgencyBadgeColor={getAgencyBadgeColor}
              formatDate={formatDate}
              formatTime={formatTime}
              getServiceTypeText={getServiceTypeText}
              handleStatusUpdate={handleStatusUpdate}
              setAssignments={setAssignments}
              renderNotesWithPhoneLinks={renderNotesWithPhoneLinks}
            />
          ))}
        </div>
      </div>
    ));
  }, [groupedAssignments, getAvailableActions, getAgencyBadgeColor, handleStatusUpdate]);

  return (
    <>
      <MaterialIcons />
      <div className="min-h-screen flex flex-col bg-gray-50" style={{fontFamily: 'Roboto, sans-serif'}}>
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-md mx-auto p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Hoşgeldiniz</p>
              <h1 className="text-xl font-bold text-gray-900">
                {user?.name || 'Şoför'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={loadAssignments}
                disabled={loading}
                className="flex items-center text-gray-500 hover:text-blue-500 disabled:opacity-50"
              >
                <span className="material-icons text-lg">refresh</span>
                <span className="ml-1 text-sm">Yenile</span>
              </button>
              <button 
                onClick={onLogout}
                className="flex items-center text-gray-500 hover:text-red-500"
              >
                <span className="material-icons text-lg">logout</span>
                <span className="ml-1 text-sm">Çıkış</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-grow p-4 max-w-md mx-auto w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Atanan Transferler ({assignments.length})
            </h2>
            <p className="text-sm text-gray-500">
              {new Date().toLocaleDateString('tr-TR', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-6 mt-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Görevler yükleniyor...</p>
              </div>
            ) : assignments.length === 0 ? (
              <EmptyState />
            ) : (
              renderGroupedAssignments
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Driver_Home;

